import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import { visuallyHidden } from "@mui/utils";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../../../Contexts/AppContext";
import { DatesUtils } from "../../../utils/DatesUtils";
import { Device } from "../../../models/Device";
import { SortOrder } from "mongoose";
import { CircularProgress } from "@mui/material";
import DateRangesFilter from "../../DeviceDetailsScreen/DeviceHistoryScreen/components/DateRangesFilter/DateRangesFilter";
import { Dayjs } from "dayjs";
import {
	ConnectivityReport,
	ConnectivityReportStatus,
} from "../../../models/ConnectivityReport";
import ConnectivityReportApi from "../../../api/connectivity-report";
import DevicesSelector from "../../../common/components/DevicesSelector/DevicesSelector";
import { Link } from "react-router-dom";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
	order: Order,
	orderBy: Key
): (
	a: { [key in Key]: number | string },
	b: { [key in Key]: number | string }
) => number {
	return order === "desc"
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

interface HeadCell {
	disablePadding: boolean;
	id: keyof ConnectivityReport;
	label: string;
	numeric: boolean;
}

const headCells: readonly (HeadCell & { align: string })[] = [
	{
		id: "deviceId",
		numeric: true,
		disablePadding: true,
		align: "center",
		label: "ID del medidor",
	},
	{
		id: "timestamp",
		numeric: true,
		disablePadding: true,
		align: "center",
		label: "Fecha de adquisicion",
	},
	{
		id: "ipAddress",
		numeric: true,
		align: "center",
		disablePadding: false,
		label: "Direccion IP",
	},
	{
		id: "port",
		numeric: true,
		align: "center",
		disablePadding: false,
		label: "Puerto",
	},
	{
		id: "status",
		numeric: true,
		align: "center",
		disablePadding: false,
		label: "Estado",
	},
];

interface EnhancedTableProps {
	numSelected: number;
	onRequestSort: (
		event: React.MouseEvent<unknown>,
		property: keyof ConnectivityReport
	) => void;
	onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
	order: Order;
	orderBy: string;
	rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
	const {
		onSelectAllClick,
		order,
		orderBy,
		numSelected,
		rowCount,
		onRequestSort,
	} = props;
	const createSortHandler =
		(property: keyof ConnectivityReport) =>
		(event: React.MouseEvent<unknown>) => {
			onRequestSort(event, property);
		};

	return (
		<TableHead>
			<TableRow>
				{headCells.map((headCell) => (
					<TableCell
						key={headCell.id}
						// align={headCell.numeric ? "right" : "left"}
						align={"center"}
						padding={headCell.disablePadding ? "none" : "normal"}
						sortDirection={orderBy === headCell.id ? order : false}>
						<TableSortLabel
							active={orderBy === headCell.id}
							direction={orderBy === headCell.id ? order : "asc"}
							onClick={createSortHandler(headCell.id)}>
							{headCell.label}
							{orderBy === headCell.id ? (
								<Box component='span' sx={visuallyHidden}>
									{order === "desc" ? "sorted descending" : "sorted ascending"}
								</Box>
							) : null}
						</TableSortLabel>
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}
interface EnhancedTableToolbarProps {
	numSelected: number;
}
function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
	const { numSelected } = props;
	const { userContext } = useContext(AppContext);

	return (
		<Toolbar
			sx={[
				{
					pl: { sm: 2 },
					pr: { xs: 1, sm: 1 },
				},
				numSelected > 0 && {
					bgcolor: (theme) =>
						alpha(
							theme.palette.primary.main,
							theme.palette.action.activatedOpacity
						),
				},
			]}>
			{numSelected > 0 ? (
				<Typography
					sx={{ flex: "1 1 100%" }}
					color='inherit'
					variant='subtitle1'
					component='div'>
					{numSelected} selected
				</Typography>
			) : (
				<>
					<Typography
						sx={{ flex: "1 1 100%" }}
						variant='h6'
						id='tableTitle'
						component='div'>
						Reporte de conectividad {userContext?.permissionaryName || ""}
					</Typography>
				</>
			)}
			{numSelected > 0 ? (
				<Tooltip title='Delete'>
					<IconButton>
						<DeleteIcon />
					</IconButton>
				</Tooltip>
			) : (
				<Tooltip title='Filter list'>
					<IconButton>
						<FilterListIcon />
					</IconButton>
				</Tooltip>
			)}
		</Toolbar>
	);
}
export default function ConnectivityReportTable() {
	const [loading, setLoading] = useState(true);
	const [rows, setRows] = useState<ConnectivityReport[]>([]);
	const [order, setOrder] = useState<Order>("asc");
	const [orderBy, setOrderBy] = useState<keyof ConnectivityReport>("timestamp");
	const [selected, setSelected] = useState<readonly number[]>([]);
	const [page, setPage] = useState(0);
	const [dense, setDense] = useState(false);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [startDate, setStartDate] = useState<Dayjs | null>(null);
	const [endDate, setEndDate] = useState<Dayjs | null>(null);
	const [device, setDevice] = useState<Device | null>(null);

	const { userContext } = useContext(AppContext);

	const fetchHistory = useCallback(async () => {
		if (!userContext?.permissionaryId || !startDate || !endDate) return;

		const limit = rowsPerPage + 1;
		const offset = page * rowsPerPage;
		const sort: Record<string, SortOrder> = {
			[orderBy]: order === "asc" ? 1 : -1,
		};

		const startDateDto = startDate.startOf("day").toDate().getTime();
		const endDateDto = endDate.endOf("day").toDate().getTime();

		try {
			setLoading(true);
			const response = await ConnectivityReportApi.getConnectivityReportData({
				accessToken: localStorage.getItem("accessToken") || "",
				searchDto: {
					query: {
						permissionaryId: userContext.permissionaryId,
						deviceId: device ? device.deviceId : undefined,
					},
					sort,
					offset,
					limit,
					timestampRange: {
						from: startDateDto,
						end: endDateDto,
					},
				},
			});
			console.log("Response => ", response);
			setRows(response);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	}, [
		userContext,
		order,
		rowsPerPage,
		page,
		orderBy,
		startDate,
		endDate,
		device,
	]);

	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof ConnectivityReport
	) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const handleClick = (event: React.MouseEvent<unknown>, id: number) => {
		const selectedIndex = selected.indexOf(id);
		let newSelected: readonly number[] = [];

		if (selectedIndex === -1) {
			newSelected = newSelected.concat(selected, id);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(selected.slice(1));
		} else if (selectedIndex === selected.length - 1) {
			newSelected = newSelected.concat(selected.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(
				selected.slice(0, selectedIndex),
				selected.slice(selectedIndex + 1)
			);
		}
		setSelected(newSelected);
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
		setDense(event.target.checked);
	};

	// Avoid a layout jump when reaching the last page with empty rows.
	const emptyRows =
		page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

	const visibleRows = useMemo(() => {
		return (
			[...rows]
				//@ts-expect-error deviceObject not included in the type
				.sort(getComparator(order, orderBy))
				.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
		);
	}, [order, orderBy, page, rowsPerPage, rows]);

	const onDateRangeChange = useCallback(
		(date: Dayjs | null, prop: "startDate" | "endDate") => {
			if (prop === "startDate") {
				setStartDate(date);
			} else if (prop === "endDate") {
				setEndDate(date);
			}
		},
		[]
	);

	useEffect(() => {
		fetchHistory();
	}, [fetchHistory]);

	return (
		<Box sx={{ width: "100%" }}>
			{loading && (
				<div className='text-center'>
					<CircularProgress size={100} />
				</div>
			)}
			<div className='text-center'>
				{/* insert back icon */}
				<DateRangesFilter
					onDateChange={(date: Dayjs | null, prop: "startDate" | "endDate") =>
						onDateRangeChange(date, prop)
					}
				/>
				<div
					style={{
						width: "50%",
						margin: "0 auto",
					}}>
					<DevicesSelector
						reactOnChange={(newDevice: Device | null) => {
							setDevice(newDevice);
						}}
					/>
				</div>
			</div>
			{!loading && (
				<Paper sx={{ width: "100%", mb: 2 }}>
					<EnhancedTableToolbar numSelected={selected.length} />
					<TableContainer>
						<Table
							aria-labelledby='tableTitle'
							size={dense ? "small" : "medium"}>
							<EnhancedTableHead
								numSelected={selected.length}
								order={order}
								orderBy={orderBy}
								onSelectAllClick={() => 0}
								onRequestSort={handleRequestSort}
								rowCount={rows.length}
							/>
							<TableBody>
								{visibleRows.map((row, index) => {
									return (
										<TableRow
											hover
											// onClick={}
											role='checkbox'
											// aria-checked={isItemSelected}
											tabIndex={-1}
											key={row._id}
											// selected={isItemSelected}
											sx={{ cursor: "pointer" }}>
											<TableCell align='center'>
												<Link
													style={{
														textDecoration: "underline",
														color: "inherit",
													}}
													to={"/devices/" + row.device?._id}>
													{row.deviceId}
												</Link>
											</TableCell>
											<TableCell align='center'>
												{DatesUtils.convertTimeStampToLocalDateTimeString(
													row.timestamp
												)}
											</TableCell>
											<TableCell align='center'>{row.ipAddress}</TableCell>
											<TableCell align='center'>{row.port}</TableCell>
											<TableCell
												style={{
													backgroundColor:
														row.status === ConnectivityReportStatus.SUCCESS
															? "green"
															: "#900",
													color: "white",
												}}
												align='center'>
												{row.status === ConnectivityReportStatus.SUCCESS
													? "En linea"
													: "Fuera de linea"}
											</TableCell>
										</TableRow>
									);
								})}
								{emptyRows > 0 && (
									<TableRow
										style={{
											height: (dense ? 33 : 53) * emptyRows,
										}}>
										<TableCell colSpan={6} />
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000, 5000]}
						component='div'
						count={rows.length}
						rowsPerPage={rowsPerPage}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				</Paper>
			)}
		</Box>
	);
}
